import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useBookingPermissions } from "@/hooks/use-booking-permissions";
import { BookingList } from "@/components/bookings/booking-list";
import { BookingForm } from "@/components/bookings/booking-form";
import { BookingDetails } from "@/components/bookings/booking-details";
import { bookingService } from "@/services/bookingService";
import { Booking, BookingStats, UserRole } from "@shared/types/booking";
import { 
  Calendar, 
  Plus,
  BarChart3,
  TrendingUp,
  Users,
  DollarSign,
  CheckCircle,
  Clock,
  XCircle
} from "lucide-react";
import SaudiRiyal from "@/components/shared/saudi-riyal";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Bookings() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  
  // Map user role to booking system roles
  const getUserRole = (roleId: number): UserRole => {
    switch (roleId) {
      case 2: // admin
        return 'admin';
      case 3: // staff
        return 'staff';
      case 1: // customer
      default:
        return 'customer';
    }
  };
  
  const userRole: UserRole = getUserRole(user?.roleId || 1);
  const permissions = useBookingPermissions(userRole);
  
  // Debug logging
  console.log('🔍 Bookings Page Debug:', {
    user,
    userRoleId: user?.roleId,
    mappedUserRole: userRole,
    permissions
  });
  
  const [stats, setStats] = useState<BookingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showBookingDetails, setShowBookingDetails] = useState(false);

  // Load booking statistics
  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        const response = await bookingService.getBookingStats();
        console.log('📊 Stats response:', response);
        if (response.success && response.data) {
          setStats(response.data);
        } else {
          console.error('Failed to get stats data:', response);
        }
      } catch (error) {
        console.error('Failed to load booking stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  const handleCreateBooking = (booking: any) => {
    setShowCreateForm(false);
    setActiveTab('list');
    toast({
      title: t('success'),
      description: t('bookingCreatedSuccessfully')
    });
  };

  const handleViewBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowBookingDetails(true);
  };

  const handleEditBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setActiveTab('edit');
  };

  const handleCancelBooking = (bookingId: number) => {
    setShowBookingDetails(false);
    setSelectedBooking(null);
    setActiveTab('list');
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight truncate">{t('bookingManagement')}</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            {t('manageHotelReservationsAndBookings')}
          </p>
        </div>
        
        {permissions.canCreate && (
          <Button onClick={() => setShowCreateForm(true)} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            {t('createBooking')}
          </Button>
        )}
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('totalBookings')}</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_bookings || 0}</div>
              <p className="text-xs text-muted-foreground">
                {t('allTimeBookings')}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('confirmedBookings')}</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.confirmed_bookings || 0}</div>
              <p className="text-xs text-muted-foreground">
                {t('activeReservations')}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('totalRevenue')}</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold">
                <SaudiRiyal amount={stats?.total_revenue || 0} variant="new" />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('fromAllBookings')}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('pendingDeposits')}</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.pending_deposits || 0}</div>
              <p className="text-xs text-muted-foreground">
                {t('awaitingPayment')}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="list">{t('allBookings')}</TabsTrigger>
          {selectedBooking && (
            <TabsTrigger value="edit">{t('editBooking')}</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="list" className="space-y-4">
          <BookingList 
            userRole={userRole} 
            userId={user?.id ? (typeof user.id === 'string' ? parseInt(user.id) : user.id) : undefined}
            onViewBooking={handleViewBooking}
            onEditBooking={handleEditBooking}
          />
        </TabsContent>


        {selectedBooking && (
          <TabsContent value="edit" className="space-y-4">
            <BookingDetails 
              bookingId={selectedBooking.id}
              userRole={userRole}
              onEdit={handleEditBooking}
              onCancel={handleCancelBooking}
            />
          </TabsContent>
        )}
      </Tabs>

      {/* Create Booking Dialog - Full Width */}
      <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
        <DialogContent className="max-w-6xl w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{t('createBooking')}</DialogTitle>
            <DialogDescription>
              {t('createNewHotelReservation')}
            </DialogDescription>
          </DialogHeader>
          <BookingForm 
            onSuccess={handleCreateBooking}
            onCancel={() => setShowCreateForm(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Booking Details Dialog */}
      <Dialog open={showBookingDetails} onOpenChange={setShowBookingDetails}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('bookingDetails')}</DialogTitle>
            <DialogDescription>
              {t('viewBookingInformation')}
            </DialogDescription>
          </DialogHeader>
          {selectedBooking && (
            <BookingDetails 
              bookingId={selectedBooking.id}
              userRole={userRole}
              onEdit={handleEditBooking}
              onCancel={handleCancelBooking}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}